import _ from 'lodash';

import { getFreqConversionFactor, getScaledEnvelope } from '../utils';
import { convertSpectrumYAxis } from '../../utils/overlayUtils';
import {
  transformSpectrumData,
  getPointShaftSpeed,
  getResponseFrequencyUnits
} from './spectrumTransformations';

/**
 * Central authority for spectrum display-unit state.
 *
 * Ownership model:
 * - The backend response (`config.spectrum`, overlay point `originalResponse`/`originalData`)
 *   is the pristine source of truth. It is never mutated by display logic.
 * - The page (e.g. ChartsView) owns a single *requested* display unit per axis.
 *   `null` means "no explicit request" and resolves to the backend unit
 *   (which is already in the user's preferred frequency unit).
 * - Everything rendered (data, features, overlay points, envelope, axis labels) is
 *   *derived* from pristine data + requested units by `deriveSpectrumDisplay`, in one
 *   place and in one pass, so data and units cannot drift apart.
 */

export const FREQUENCY_UNITS = ['Hz', 'CPM', 'Orders'];

// Shared by ChartsView and the overlay controller so they don't drift.
export const preferredSpectrumXUnit = currentUser =>
  (currentUser && currentUser.spectrum_x_scale === 'orders' ? 'Orders' : null);

const MIN_SHAFT_SPEED = 0.001;

const ORDERS_CAPABLE_TAG_TYPES = ['vibration', 'current'];

export const isValidShaftSpeed = speed => (
  typeof speed === 'number' && Number.isFinite(speed) && speed > MIN_SHAFT_SPEED
);

const pointSpeedOf = (point) => {
  if (!point) return undefined;
  if (point.shaft_speed !== undefined) return point.shaft_speed;
  if (point.speed !== undefined) return point.speed;
  return point.referenceShaftSpeed;
};

/**
 * Whether the Orders unit can currently be rendered.
 * Orders requires a usable shaft speed; in overlay mode every displayed point
 * must have a usable speed (a zero-speed measurement cannot be normalized).
 */
export const canDisplayOrders = ({ shaftSpeed, tagType, selectedPoints, showOverlay }) => {
  if (tagType && ORDERS_CAPABLE_TAG_TYPES.indexOf(tagType) === -1) return false;

  if (showOverlay && Array.isArray(selectedPoints) && selectedPoints.length > 0) {
    return selectedPoints.every((point) => {
      const speed = pointSpeedOf(point);
      // Points without their own speed may fall back to the chart-level speed
      if (speed === undefined || speed === null) return isValidShaftSpeed(shaftSpeed);
      return isValidShaftSpeed(speed);
    });
  }

  return isValidShaftSpeed(shaftSpeed);
};

/**
 * Resolves the requested X-axis unit into a renderable one.
 * Falls back to the backend unit (never a hardcoded unit) when the request
 * is absent or cannot be honored (e.g. Orders without a shaft speed).
 */
export const resolveSpectrumXUnit = ({
  requestedXUnit,
  backendXUnit,
  shaftSpeed,
  tagType,
  selectedPoints,
  showOverlay
}) => {
  const fallback = backendXUnit || 'Hz';
  if (!requestedXUnit || FREQUENCY_UNITS.indexOf(requestedXUnit) === -1) return fallback;
  if (requestedXUnit === 'Orders' &&
    !canDisplayOrders({ shaftSpeed, tagType, selectedPoints, showOverlay })) {
    return fallback;
  }
  return requestedXUnit;
};

/**
 * Resolves the requested Y-axis (amplitude) unit. The only display-side amplitude
 * transform is dB; any non-dB request follows the backend amplitude unit, so a stale
 * linear request (e.g. 'mm/s' after switching to acceleration) cannot mislabel data.
 */
export const resolveSpectrumYUnit = ({ requestedYUnit, backendYUnit }) => {
  if (requestedYUnit && String(requestedYUnit).toLowerCase() === 'db') return requestedYUnit;
  return backendYUnit || requestedYUnit || '';
};

/**
 * The user-facing toggle cycle: Hz -> CPM -> Orders -> Hz, skipping Orders
 * when it cannot currently be displayed.
 *
 * Displayability is decided by the same `canDisplayOrders` the resolver uses, so
 * the cycle and `resolveSpectrumXUnit` never disagree. This matters because the
 * cycle is driven by the *displayed* unit but written against the *requested*
 * unit: if it proposed `Orders` while Orders isn't displayable (e.g. an overlay
 * point lacks a usable speed) the request would already equal `Orders` and the
 * toggle would no-op, leaving the user stuck on the fallback unit.
 */
export const nextSpectrumXUnit = (currentXUnit, {
  shaftSpeed,
  tagType,
  selectedPoints,
  showOverlay
} = {}) => {
  if (currentXUnit === 'Hz') return 'CPM';
  if (currentXUnit === 'CPM') {
    return canDisplayOrders({ shaftSpeed, tagType, selectedPoints, showOverlay }) ? 'Orders' : 'Hz';
  }
  return 'Hz';
};

/**
 * Converts feature/marker items to the display unit using each item's own
 * `freq_units` as the source. Items that cannot be converted (e.g. to Orders
 * without a usable speed) are returned unchanged with their original
 * `freq_units` so draw-time alignment can still treat them correctly.
 */
const convertFeatureItems = (items, targetUnit, shaftSpeed, shaftSpeedUnit, defaultSourceUnit) => (
  items.map((item) => {
    const sourceUnit = item.freq_units || defaultSourceUnit || 'Hz';
    if (sourceUnit === targetUnit) return _.cloneDeep(item);
    if ((sourceUnit === 'Orders' || targetUnit === 'Orders') && !isValidShaftSpeed(shaftSpeed)) {
      return _.cloneDeep(item);
    }

    const factor = getFreqConversionFactor(sourceUnit, targetUnit, shaftSpeed, shaftSpeedUnit);
    if (!Number.isFinite(factor) || factor <= 0) return _.cloneDeep(item);

    const converted = _.cloneDeep(item);
    ['frequency', 'frequency_start', 'frequency_end'].forEach((key) => {
      if (typeof converted[key] === 'number' && Number.isFinite(converted[key])) {
        const value = converted[key] * factor;
        if (Number.isFinite(value)) converted[key] = value;
      }
    });
    converted.freq_units = targetUnit;
    return converted;
  })
);

const convertFeatures = (features, targetUnit, shaftSpeed, shaftSpeedUnit, defaultSourceUnit) => {
  if (!features || !Array.isArray(features.items)) return features;
  return {
    ...features,
    items: convertFeatureItems(features.items, targetUnit, shaftSpeed, shaftSpeedUnit, defaultSourceUnit)
  };
};

/**
 * Derives the display version of a single overlay point from its pristine fields
 * (`originalData`/`originalResponse`/`originalFeatures`). Only pristine fields are
 * trusted: any previously stored converted `spectrum_data`/`xUnit` is ignored, which
 * makes the derivation idempotent and immune to stale stored conversions.
 */
export const deriveOverlayPointDisplay = (point, {
  xUnit,
  yUnit,
  backendXUnit,
  backendYUnit,
  fallbackShaftSpeed
}) => {
  if (!point) return point;

  const pristineData = point.originalData || point.spectrum_data;
  if (!pristineData || !Array.isArray(pristineData) || pristineData.length === 0) {
    // Spectrum not fetched yet; nothing to derive.
    return point;
  }

  const sourceXUnit = getResponseFrequencyUnits(point.originalResponse, backendXUnit || 'Hz');
  const sourceYUnit = (point.originalResponse && point.originalResponse.amp_units) ||
    point.yUnit || backendYUnit || '';
  const pointShaftSpeed = getPointShaftSpeed(point, fallbackShaftSpeed);

  let data = _.cloneDeep(pristineData);
  data = convertSpectrumYAxis(data, sourceYUnit, yUnit);
  data = transformSpectrumData(data, sourceXUnit, xUnit, pointShaftSpeed, null, null, sourceXUnit);

  const pristineFeatures = point.originalFeatures || point.spectrumFeatures;
  const displayFeatures = pristineFeatures
    ? convertFeatures(pristineFeatures, xUnit, pointShaftSpeed, sourceXUnit, sourceXUnit)
    : pristineFeatures;

  return {
    ...point,
    spectrum_data: data,
    spectrumFeatures: displayFeatures,
    xUnit,
    yUnit,
    // Preserve pristine fields so future derivations remain exact
    originalData: pristineData,
    originalFeatures: pristineFeatures,
    originalResponse: point.originalResponse || {
      freq_units: sourceXUnit,
      amp_units: sourceYUnit
    },
    referenceShaftSpeed: pointShaftSpeed
  };
};

/**
 * Derives everything the spectrum chart renders from pristine inputs in one pass.
 *
 * @param {Object} params
 * @param {Object} [params.spectrum] - Pristine backend spectrum (`config.spectrum`):
 *   `{ spectrum_data, freq_units, amp_units, shaft_speed, ... }`
 * @param {Object} [params.features] - Pristine features `{ items }` (config features +
 *   forcing frequencies), each item carrying its own `freq_units`
 * @param {string|null} [params.requestedXUnit] - Requested display unit or null (follow backend)
 * @param {string|null} [params.requestedYUnit] - Requested amplitude unit or null (follow backend)
 * @param {string} [params.tagType]
 * @param {Array} [params.selectedPoints] - Overlay points carrying pristine fields
 * @param {boolean} [params.showOverlay]
 * @param {Object} [params.spectralEnvelope] - Pristine spectral envelope
 * @param {string} [params.envelopeXUnit] - Unit of the pristine envelope frequencies
 * @returns {Object} `{ xUnit, yUnit, backendXUnit, backendYUnit, shaftSpeed, spectrumData,
 *   features, selectedPoints, spectralEnvelope }` — all expressed in the resolved units
 */
export const deriveSpectrumDisplay = ({
  spectrum,
  features,
  requestedXUnit,
  requestedYUnit,
  tagType,
  selectedPoints,
  showOverlay,
  spectralEnvelope,
  envelopeXUnit
}) => {
  const backendXUnit = (spectrum && spectrum.freq_units) || 'Hz';
  const backendYUnit = (spectrum && spectrum.amp_units) || '';
  const shaftSpeed = spectrum ? spectrum.shaft_speed : null;

  const xUnit = resolveSpectrumXUnit({
    requestedXUnit,
    backendXUnit,
    shaftSpeed,
    tagType,
    selectedPoints,
    showOverlay
  });
  const yUnit = resolveSpectrumYUnit({ requestedYUnit, backendYUnit });

  // Main spectrum series
  let spectrumData = null;
  if (spectrum && Array.isArray(spectrum.spectrum_data)) {
    spectrumData = _.cloneDeep(spectrum.spectrum_data);
    spectrumData = convertSpectrumYAxis(spectrumData, backendYUnit, yUnit);
    spectrumData = transformSpectrumData(
      spectrumData, backendXUnit, xUnit, shaftSpeed, null, null, backendXUnit
    );
  }

  // Features / markers
  const displayFeatures = features
    ? convertFeatures(features, xUnit, shaftSpeed, backendXUnit, backendXUnit)
    : features;

  // Overlay points (each normalized by its own shaft speed in Orders mode)
  const displayPoints = Array.isArray(selectedPoints)
    ? selectedPoints.map(point => deriveOverlayPointDisplay(point, {
      xUnit,
      yUnit,
      backendXUnit,
      backendYUnit,
      fallbackShaftSpeed: shaftSpeed
    }))
    : selectedPoints;

  // Spectral envelope (alarm bands)
  let displayEnvelope = spectralEnvelope;
  if (spectralEnvelope && spectralEnvelope.envelope_data) {
    const envelopeSourceUnit = envelopeXUnit ||
      spectralEnvelope.envelope_data.x_units ||
      backendXUnit;
    displayEnvelope = getScaledEnvelope(
      spectralEnvelope, envelopeSourceUnit, xUnit, shaftSpeed, backendXUnit
    );
  }

  return {
    xUnit,
    yUnit,
    backendXUnit,
    backendYUnit,
    shaftSpeed,
    spectrumData,
    features: displayFeatures,
    selectedPoints: displayPoints,
    spectralEnvelope: displayEnvelope
  };
};
